import { AppFeaturesKeys } from '@feature-flags';
import { TimelineDetailContainerComponent } from 'apps/phoenix/src/app/features/direct-collection/components/timeline-detail-container/timeline-detail-container.component';
import { DirectCollectService } from 'apps/phoenix/src/app/features/direct-collection/services/direct-collect.service';
import { PhoenixTrpcClient, RouterInput } from 'apps/phoenix/src/app/utils';
import { from } from 'rxjs';

import {
  Component,
  EventEmitter,
  HostListener,
  Output,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { FeatureFlagsService } from '../../../../services';
import { CommandPage, getRootPages } from '../../../interfaces';
import { PermissionsPipe } from '../../../pipes';
import { UiHelpersService } from '../../../services';
import { UiModule } from '../../../ui.module';

@Component({
  selector: 'phoenix-sub-command-dialog',
  templateUrl: './sub-command-dialog.component.html',
  standalone: true,
  imports: [UiModule, TimelineDetailContainerComponent],
  styleUrl: './sub-command-dialog.component.scss',
})
export class SubCommandDialogComponent {
  readonly rootPages: CommandPage[] = getRootPages(
    this.openDirectCollect.bind(this),
    this.getThreatEntities.bind(this)
  );

  pages: Array<CommandPage> = [
    {
      id: 'phoenix',
      label: 'Phoenix',
      action: 'none',
      placeholder: 'What would you like to do?',
      autocomplete: this.simpleFilter.bind(this),
    },
  ];

  items: Array<CommandPage> = this.rootPages;
  _filteredItems: Array<CommandPage> = [];

  commandSearchControl = new FormControl('');
  selectedItemIndex = 0;
  styleTransform = '';
  isLoading = false;
  skip = 0;
  take = 5;

  timelineSelectedItem: any;

  private readonly _router: Router = inject(Router);
  phoenixTrpc = inject(PhoenixTrpcClient);
  dcService = inject(DirectCollectService);
  uiHelper = inject(UiHelpersService);
  commandDialog = inject(MatDialog);
  permissionsPipe = inject(PermissionsPipe);
  private readonly _featureFlags = inject(FeatureFlagsService);

  @Output() isDialogOpen = new EventEmitter<boolean>();

  get activePage() {
    return this.pages[this.pages.length - 1];
  }

  set filteredItems(value: CommandPage[]) {
    Promise.all(
      value.map(async page => ({
        page,
        hasPermission: await this.permissionsPipe.transform(
          page.permissions || []
        ),
        hasFeatureFlag: this.checkFeatureFlag(page.featureFlag),
      }))
    ).then(results => {
      this._filteredItems = results
        .filter(result => result.hasPermission && result.hasFeatureFlag)
        .map(result => result.page)
        .slice(0, 5);
      this.selectedItemIndex = 0;
    });
  }

  get filteredItems() {
    return this._filteredItems;
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    if (this.handleThreatEntityInput(event)) return;
    if (this.handleArrowKeys(event)) return;
    if (this.handleShortcuts()) return;
    if (this.handleBackspace(event)) return;
    if (this.handleEnterKey(event)) return;

    this.activePage.autocomplete?.(this.commandSearchControl.value ?? '');
  }

  simpleFilter(input?: string) {
    if (!input) {
      this.filteredItems = this.items;
      return;
    }

    const value = input?.toLowerCase();
    this.filteredItems = this.items.filter(page =>
      page.label.toLowerCase().includes(value)
    );
  }

  handleThreatEntityInput(event: KeyboardEvent) {
    if (this.activePage.id === 'threat-entity') {
      if (
        event.key === 'Meta' ||
        event.key === 'Control' ||
        event.key === 'Shift' ||
        event.key === 'Alt'
      ) {
        return true;
      }
    }
    return false;
  }

  handleBackspace(event: KeyboardEvent) {
    if (
      event.key === 'Backspace' &&
      this.commandSearchControl.value === '' &&
      this.pages.length > 1
    ) {
      event.preventDefault();
      this.popPage();
      if (this.activePage.id === 'phoenix') {
        this.timelineSelectedItem = null;
        const openDialogs = this.commandDialog.openDialogs;
        if (openDialogs.length > 0) {
          const dialogRef = openDialogs[openDialogs.length - 1];
          dialogRef.updateSize('500px', 'auto');
        }
      }
      this.bounce();
      return true;
    }
    return false;
  }

  popPage() {
    if (this.pages.length === 1) return;
    this.pages.pop();

    if (this.pages.length === 1) {
      this.items = this.rootPages;
      this.filteredItems = this.items;
    }
  }

  pushPage(page: CommandPage) {
    if (this.pages.some(existingPage => existingPage.id === page.id)) {
      return;
    }
    this.pages.push(page);
    this.commandSearchControl.setValue('');
    this.items = [];
    this.filteredItems = this.items;
  }

  bounce() {
    this.styleTransform = 'scale(0.96)';
    setTimeout(() => {
      this.styleTransform = '';
    }, 100);
  }

  handleShortcuts() {
    if (!this.commandSearchControl.value?.length) {
      return false;
    }

    const key = this.commandSearchControl.value?.slice(0, 1);
    for (const page of this.items) {
      if (page.shortcut === key) {
        this.doActionOnPage(page);
        return true;
      }
    }

    return false;
  }

  navigateOut(url: string, queryParams: Record<string, string>) {
    this._router.navigate([url], { queryParams });
    this.isDialogOpen.emit(false);
    this.commandDialog.closeAll();
  }

  handleArrowKeys(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        this.selectedItemIndex =
          (this.selectedItemIndex + 1) % this.filteredItems.length;
        return true;
      case 'ArrowUp':
        this.selectedItemIndex =
          (this.selectedItemIndex - 1 + this.filteredItems.length) %
          this.filteredItems.length;
        return true;
    }
    return false;
  }

  selectItem(index: number) {
    this.selectedItemIndex = index;
    const selectedItem = this.filteredItems[this.selectedItemIndex];

    if (selectedItem) {
      this.doActionOnPage(selectedItem);
    }
  }

  handleEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      const selectedItem = this.filteredItems[this.selectedItemIndex];
      const active = selectedItem ?? this.activePage;

      this.doActionOnPage(active);
      return true;
    }
    return false;
  }

  doActionOnPage(page: CommandPage) {
    switch (page.action) {
      case 'change-page':
        this.pushPage(page);
        break;
      case 'open':
        page.open?.(this.commandSearchControl.value ?? '');
        break;
      case 'navigate':
        page.url && this.navigateOut(page.url, page.queryParams ?? {});
        break;
    }
  }

  openDirectCollect(directCollectId?: string) {
    if (this.activePage.id !== 'direct-collect') {
      return this.pushPage(
        this.rootPages.find(p => p.id === 'direct-collect')!
      );
    }

    if (!directCollectId) {
      return this.navigateOut('/direct-collection', {});
    }

    from(
      this.phoenixTrpc.directCollect.get.query({
        id: directCollectId,
      })
    ).subscribe({
      next: (data: any) => {
        let type: string = '';
        if (data.node?.message) {
          type = 'MESSAGE';
        } else if (data.node?.screenshot) {
          type = 'SCREENSHOT';
        }

        const formattedData = { ...data, type: type };
        this.timelineSelectedItem = this.uiHelper.formatResponse(formattedData);

        const openDialogs = this.commandDialog.openDialogs;
        if (openDialogs.length > 0) {
          const dialogRef = openDialogs[openDialogs.length - 1];
          dialogRef.updateSize('100%', '90vh');
        }
      },
      error: error => {
        this.timelineSelectedItem = null;
        const openDialogs = this.commandDialog.openDialogs;
        if (openDialogs.length > 0) {
          const dialogRef = openDialogs[openDialogs.length - 1];
          dialogRef.updateSize('500px', 'auto');
        }
        this.uiHelper.handleErrorMessage(
          error,
          'Invalid Direct Collect ID. Please check the ID and try again.'
        );
      },
    });
  }

  getThreatEntities(searchValue?: string) {
    if (this.activePage.id !== 'threat-entity') {
      return this.pushPage(this.rootPages.find(p => p.id === 'threat-entity')!);
    }

    if (!searchValue) {
      this.navigateOut('/threat-entity', {});
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    const params: RouterInput['threatEntity']['list'] = {
      take: this.take,
      skip: this.skip,
      filters: {
        search: searchValue,
      },
    };

    from(this.phoenixTrpc.threatEntity.list.query(params)).subscribe({
      next: result => {
        this.items = result.edges.map((edge: any) => ({
          id: edge.node.id,
          label: edge.node.name,
          action: 'navigate',
          url: `/threat-entity`,
          queryParams: { id: edge.node.id },
        }));
        this.filteredItems = this.items;
        if (this.activePage.id === 'phoenix') {
          this.items = this.rootPages;
          this.filteredItems = this.rootPages;
        }
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.uiHelper.handleErrorMessage(
          error,
          'Invalid Threat Entity. Please check and try again.'
        );
      },
    });
  }

  public checkFeatureFlag(
    featureFlag: AppFeaturesKeys | AppFeaturesKeys[] | undefined
  ): boolean {
    if (Array.isArray(featureFlag)) {
      return featureFlag.some(flag => this._featureFlags.isOn(flag));
    }

    if (featureFlag) {
      return this._featureFlags.isOn(featureFlag);
    }

    return true;
  }
}
